import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { forwardRef, useImperativeHandle, useEffect } from "react";
import { FormField } from "@/components/ui/FormField";

export const zoneSchema = z.object({
  Name: z.string().min(2, "Zone name must be at least 2 characters"),
  Description: z.string().optional(),
});

export type ZoneFormData = z.infer<typeof zoneSchema>;

export interface AddZoneFormHandle {
  submit: () => void;
  setFieldError: (field: string, message: string) => void;
  clearFieldErrors: () => void;
}

interface AddZoneFormProps {
  onSubmit: (data: ZoneFormData) => void;
  defaultValues?: ZoneFormData;
  isLoading?: boolean;
}

const AddZoneForm = forwardRef<AddZoneFormHandle, AddZoneFormProps>(
  ({ onSubmit, defaultValues, isLoading }, ref) => {
    const {
      control,
      handleSubmit,
      formState: { errors },
      clearErrors,
      setError,
      reset,
    } = useForm<ZoneFormData>({
      resolver: zodResolver(zoneSchema),
      defaultValues: defaultValues || {
        Name: "",
        Description: "",
      },
    });

    // Reset form when defaultValues change (for edit mode)
    useEffect(() => {
      if (defaultValues) {
        reset(defaultValues);
      }
    }, [defaultValues, reset]);

    useImperativeHandle(ref, () => ({
      submit: () => {
        handleSubmit(onSubmit)();
      },
      setFieldError: (field: string, message: string) => {
        setError(field as keyof ZoneFormData, { type: "server", message });
      },
      clearFieldErrors: () => clearErrors(),
    }));

    return (
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={control}
          label="Zone Name"
          name="Name"
          error={errors.Name?.message}
          required
          disabled={isLoading}
          onClear={() => clearErrors("Name")}
        />

        <FormField
          control={control}
          label="Description"
          name="Description"
          error={errors.Description?.message}
          placeholder="Description"
          isTextarea
          disabled={isLoading}
          onClear={() => clearErrors("Description")}
        />
      </form>
    );
  }
);

AddZoneForm.displayName = "AddZoneForm";

export default AddZoneForm;
